import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Package, User, Landmark, FileText, Loader2 } from "lucide-react";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const hasCompany = typeof window !== "undefined" && !!localStorage.getItem("company_id");

  useEffect(() => {
    if (!q || q.length < 2 || !hasCompany) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get("/search", { params: { q } }); setResults(data); setOpen(true); }
      catch {} finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, hasCompany]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.querySelector("input")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  const totalHits = results ? (results.products.length + results.customers.length + results.suppliers.length + results.invoices.length) : 0;

  return (
    <div className="relative flex-1 max-w-md" ref={ref} data-testid="global-search">
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onFocus={()=>results && setOpen(true)}
          placeholder={hasCompany ? "بحث سريع في كل شي..." : "اختر شركة أولاً للبحث"}
          disabled={!hasCompany}
          className="ps-9 h-9"
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {open && results && (
        <div className="absolute top-full mt-1 start-0 end-0 bg-card border border-border rounded-md shadow-lg max-h-[500px] overflow-auto z-50" data-testid="search-results">
          {totalHits === 0 && <div className="p-4 text-center text-sm text-muted-foreground">لا نتائج</div>}
          {results.products.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-muted-foreground bg-muted/50">أصناف</div>
              {results.products.map(p => (
                <button key={p.id} onClick={()=>go("/products")} data-testid={`search-product-${p.id}`}
                  className="w-full text-start px-3 py-2 hover:bg-accent flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">رصيد: {p.stock} · {p.barcode}</div>
                  </div>
                  <div className="text-xs font-bold tabular-nums">{fmtMoney(p.sale_price, p.currency)}</div>
                </button>
              ))}
            </div>
          )}
          {results.customers.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-muted-foreground bg-muted/50">عملاء</div>
              {results.customers.map(c => (
                <button key={c.id} onClick={()=>go("/customers")} className="w-full text-start px-3 py-2 hover:bg-accent flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.phone || "—"}</div>
                  </div>
                  {c.balance ? <div className={`text-xs font-bold ${c.balance>0?"text-destructive":"text-emerald-600"} tabular-nums`}>{c.balance.toLocaleString("ar-EG")}</div> : null}
                </button>
              ))}
            </div>
          )}
          {results.suppliers.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-muted-foreground bg-muted/50">موردون</div>
              {results.suppliers.map(s => (
                <button key={s.id} onClick={()=>go("/suppliers")} className="w-full text-start px-3 py-2 hover:bg-accent flex items-center gap-2 text-sm">
                  <Landmark className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.phone || "—"}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {results.invoices.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-muted-foreground bg-muted/50">فواتير</div>
              {results.invoices.map(i => (
                <button key={i.id} onClick={()=>go("/invoices")} className="w-full text-start px-3 py-2 hover:bg-accent flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">#{i.number} — {i.party_name}</div>
                    <div className="text-xs text-muted-foreground">{i.kind === "sale" ? "بيع" : "شراء"} · {new Date(i.created_at).toLocaleDateString("ar-EG")}</div>
                  </div>
                  <div className="text-xs font-bold tabular-nums">{fmtMoney(i.total, i.currency)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
